import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  Breadcrumbs,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { BASE_PATH_TO_HOME } from "../../../base_routes_paths/data_paths";
import { BASE_PATH_TO_LEADERBOARD, BASE_PATH_TO_ALL_LEADERBOARDS } from "../../../base_routes_paths/corporate_challenges_paths";
import AUTH_CONTEXT from "../../../store";
import ARENO_API_ENDPOINT from "../../../api";

// ── Theme ─────────────────────────────────────────────────────────────────────
const BG      = "#0d0d14";
const CARD_BG = "#13131f";
const BORDER  = "#2a2a3d";
const PURPLE  = "#7c3aed";
const GLOW    = "rgba(124,58,237,0.35)";
const PRI     = "#ffffff";
const SEC     = "#8b8ba0";

// ─── Corporate ID Card ────────────────────────────────────────────────────────

const CorporateCard = ({ corporateID, imageUrl, onViewLeaderboard }) => {
  const img = imageUrl ?? null;

  return (
  <Box
    onClick={onViewLeaderboard}
    sx={{
      width: 400,
      borderRadius: 2,
      border: `1px solid ${BORDER}`,
      backgroundColor: CARD_BG,
      overflow: "hidden",
      cursor: "pointer",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: PURPLE,
        boxShadow: `0 0 24px ${GLOW}`,
        transform: "translateY(-2px)",
      },
    }}
  >
    {/* Card image / header */}
    <Box
      sx={{
        position: "relative",
        height: 200,
        background: img
          ? `url(${img}) center/cover no-repeat`
          : `linear-gradient(135deg, ${PURPLE}cc 0%, #1a0d2e 100%)`,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      {/* Dark overlay so text is always readable */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.15) 100%)",
        }}
      />
      {/* Corporate ID label pinned to bottom-left */}
      <Box sx={{ position: "absolute", bottom: 0, left: 0, px: 2.5, py: 2 }}>
        <Typography variant="h4" fontWeight={900} color={PRI} lineHeight={1.2}>
          {corporateID}
        </Typography>
      </Box>
    </Box>

    {/* Card footer */}
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 3, py: 2 }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ color: SEC }}>
        View leaderboard standings
      </Typography>
      <ArrowForwardRoundedIcon sx={{ color: PURPLE, fontSize: 20 }} />
    </Stack>
  </Box>
  );
};

// ─── Skeleton Card ────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <Box
    sx={{
      width: 400,
      borderRadius: 2,
      border: `1px solid ${BORDER}`,
      backgroundColor: CARD_BG,
      overflow: "hidden",
    }}
  >
    <Skeleton variant="rounded" width="100%" height={200} sx={{ bgcolor: "#1e1e30" }} />
    <Box sx={{ px: 3, py: 2 }}>
      <Skeleton variant="text" width="70%" sx={{ bgcolor: "#1e1e30" }} />
    </Box>
  </Box>
);

// ─── Combined Leaderboard Card ────────────────────────────────────────────────

const CombinedLeaderboardCard = ({ onViewLeaderboard }) => (
  <Box
    onClick={onViewLeaderboard}
    sx={{
      width: 824,
      borderRadius: 2,
      border: `1px solid ${BORDER}`,
      backgroundColor: CARD_BG,
      overflow: "hidden",
      cursor: "pointer",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: PURPLE,
        boxShadow: `0 0 24px ${GLOW}`,
        transform: "translateY(-2px)",
      },
    }}
  >
    {/* Card image / header */}
    <Box
      sx={{
        position: "relative",
        height: 280,
        background: `url(/clash.png) center/cover no-repeat`,
        borderBottom: `1px solid ${BORDER}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.15) 100%)",
        }}
      />
      <Box sx={{ position: "absolute", bottom: 0, left: 0, px: 2.5, py: 2 }}>
        <Typography variant="h4" fontWeight={900} color={PRI} lineHeight={1.2}>
          Clash Leaderboards
        </Typography>
      </Box>
    </Box>

    {/* Card footer */}
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 3, py: 2 }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ color: SEC }}>
        View combined standings across all groups
      </Typography>
      <ArrowForwardRoundedIcon sx={{ color: PURPLE, fontSize: 20 }} />
    </Stack>
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

const CorporateLeaderboard = () => {
  const { token } = AUTH_CONTEXT();
  const navigate  = useNavigate();

  const [corporateIDs, setCorporateIDs]       = useState([]);
  const [corporateImages, setCorporateImages] = useState([]);
  const [isLoading, setIsLoading]             = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${ARENO_API_ENDPOINT}/challenges/AdminGetCompetetiveChallengeConfig`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", authorization: `Bearer ${token}` },
          }
        );
        const data = await response.json();
        if (response.status === 200 && data.status === 200) {
          setCorporateIDs(data.data?.CorporateIDs ?? []);
          setCorporateImages(data.data?.CorporateImages ?? []);
        } else {
          toast.error(data.error || "Failed to load challenge config", {
            position: "bottom-left", autoClose: 3000, hideProgressBar: true,
          });
        }
      } catch (error) {
        toast.error(error.message, { position: "bottom-left", autoClose: 5000, theme: "light" });
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, [token]);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: BG, px: 3, py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3, "& *": { color: `${SEC} !important` }, "& .MuiBreadcrumbs-separator": { color: `${BORDER} !important` } }}>
        <Link underline="hover" href={BASE_PATH_TO_HOME} sx={{ color: SEC }}>Dashboard</Link>
        <Link underline="hover" sx={{ color: SEC }}>Corporate Challenges</Link>
        <Typography fontWeight={500} sx={{ color: PRI }}>Leaderboard</Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={5}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, backgroundColor: PURPLE, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 16px ${GLOW}` }}>
          <LeaderboardIcon sx={{ color: "#fff", fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2} color={PRI}>
            Corporate Leaderboard
          </Typography>
          <Typography variant="body2" sx={{ color: SEC }}>
            Select a corporate group to view standings
          </Typography>
        </Box>
      </Stack>

      {/* Cards — row 1: Clash card centered, row 2: corporate ID cards */}
      <Stack direction="column" gap={3} alignItems="center">
        {/* Row 1 — Clash Leaderboards */}
        <CombinedLeaderboardCard onViewLeaderboard={() => navigate(BASE_PATH_TO_ALL_LEADERBOARDS)} />

        {/* Row 2 — Corporate ID cards */}
        <Stack direction="row" flexWrap="wrap" gap={3} justifyContent="center">
          {isLoading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : corporateIDs.length === 0 ? (
            <Box sx={{ py: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <LeaderboardIcon sx={{ fontSize: 48, color: SEC, opacity: 0.3 }} />
              <Typography variant="body1" fontWeight={500} sx={{ color: SEC }}>
                No corporate groups found
              </Typography>
            </Box>
          ) : (
            corporateIDs.map((id, index) => (
              <CorporateCard
                key={id}
                corporateID={id}
                imageUrl={corporateImages[index] ?? null}
                onViewLeaderboard={() => navigate(`${BASE_PATH_TO_LEADERBOARD}/${id}`)}
              />
            ))
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default CorporateLeaderboard;
